import React, { useState, useEffect } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    Box,
    Button,
    Checkbox,
} from '@mui/material';
import {
    Download as DownloadIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
} from '@mui/icons-material';
import { saveAs } from 'file-saver';
import { getChecklistResponsesByEvent } from 'core/API/checklist';

// Define the structure of a checklist item
interface ChecklistItem {
    id: string;
    section: string;
    duty: string;
    instructions: string;
    answerType: string;
    correctAnswer: string;
    submittedAnswer: string;
    comment: string;
    picLink: string;
    isCorrect?: boolean; // Optional field to store correctness
}

interface TemplatePageProps {
    radioValue?: string;
    dropdownValue?: string;
    initialData?: ChecklistItem[];
    onBackClick?: () => void;
    accessibility?: string;
    selectedItem?: any; // Add selectedItem prop
}

const CompletedTemplatePage: React.FC<TemplatePageProps> = ({
    radioValue,
    dropdownValue,
    onBackClick,
    initialData = [],
    accessibility = 'completed',
    selectedItem,
}) => {
    const columns = [
        { id: 'section', header: 'Section' },
        { id: 'duty', header: 'Duty' },
        { id: 'instructions', header: 'Instructions' },
        { id: 'answerType', header: 'Answer' },
        { id: 'correctAnswer', header: 'Correct' },
        { id: 'submittedAnswer', header: 'Submitted' },
        { id: 'picLink', header: 'Picture' },
    ];

    const [items, setItems] = useState<ChecklistItem[]>([]);

    useEffect(() => {
        const fetchChecklistResponses = async () => {
            // If we have a selectedItem with an id, fetch checklist responses
            if (selectedItem?.id) {
                try {
                    const responseData = await getChecklistResponsesByEvent(selectedItem.id);
                    if (responseData?.member && responseData.member.length > 0) {
                        // Map response data to checklist items
                        const mappedItems = responseData.member.map((response: any, index: number) => {
                            const task = response.task || {};
                            const expectedAnswer = task.expectedAnswer || response.expectedAnswer || 'N/A';
                            const submittedAnswer = response.answer || 'N/A';
                            const isCorrect = expectedAnswer === submittedAnswer;
                            
                            return {
                                id: (index + 1).toString(),
                                section: task.section || 'Unknown Section',
                                duty: task.duty || 'Unknown Duty',
                                instructions: task.instructions || 'No instructions',
                                answerType: task.answerType || 'True/False',
                                correctAnswer: expectedAnswer,
                                submittedAnswer: submittedAnswer,
                                comment: response.reason || 'No comments',
                                picLink: '', // Picture link if available in response
                                isCorrect: isCorrect, // Store correctness for display
                            };
                        });
                        setItems(mappedItems);
                        return;
                    }
                } catch (error) {
                    console.error('Error fetching checklist responses:', error);
                    // Fall through to other data sources
                }
            }
            
            // Fallback to existing logic
            if (initialData.length > 0) {
                setItems(initialData);
            } else if (selectedItem?.checklistTemplate?.tasks && selectedItem.checklistTemplate.tasks.length > 0) {
                // Use tasks from the selected completed checklist
                const mappedTasks = selectedItem.checklistTemplate.tasks.map((task: any, index: number) => ({
                    id: (index + 1).toString(),
                    section: task.section || 'Unknown Section',
                    duty: task.duty || 'Unknown Duty',
                    instructions: task.instructions || 'No instructions',
                    answerType: task.answerType || 'True/False',
                    correctAnswer: task.expectedAnswer || 'N/A',
                    submittedAnswer: task.expectedAnswer || 'N/A', // For completed checklists, submitted answer is same as expected
                    comment: task.comment || 'No comments',
                    picLink: task.picLink || '',
                }));
                setItems(mappedTasks);
            } else {
                // Dummy data for demonstration
                setItems([
                    {
                        id: '1',
                        section: 'Safety',
                        duty: 'Fire Extinguisher Check',
                        instructions: 'Ensure all extinguishers are accessible',
                        answerType: 'True/False',
                        correctAnswer: 'True',
                        submittedAnswer: 'True',
                        comment: 'All units operational',
                        picLink: 'http://example.com/pic1.jpg',
                    },
                    {
                        id: '2',
                        section: 'Equipment',
                        duty: 'Inspect machinery',
                        instructions: 'Check for wear and tear',
                        answerType: 'Checkbox',
                        correctAnswer: 'Checked',
                        submittedAnswer: 'Unchecked',
                        comment: 'Minor wear detected',
                        picLink: 'http://example.com/pic2.jpg',
                    },
                    {
                        id: '3',
                        section: 'Environment',
                        duty: 'Verify lighting',
                        instructions: 'Ensure all lights are working',
                        answerType: 'True/False',
                        correctAnswer: 'True',
                        submittedAnswer: 'False',
                        comment: 'Two bulbs need replacement',
                        picLink: 'http://example.com/pic3.jpg',
                    },
                ]);
            }
        };
        
        fetchChecklistResponses();
    }, [initialData, selectedItem]);

    const getDataAsJson = () =>
        items.map((item) => ({
            section: item.section,
            duty: item.duty,
            instructions: item.instructions,
            answer: item.answerType,
            correct: item.correctAnswer,
            submitted: item.submittedAnswer,
            comments: item.comment,
            picture: item.picLink,
        }));

    const downloadJson = () => {
        const jsonData = getDataAsJson();
        const jsonString = JSON.stringify(jsonData, null, 2);
        const blob = new Blob([jsonString], { type: 'application/json' });
        saveAs(blob, 'checklist_data.json');
    };

    const templateTitle = radioValue ? `${radioValue} Checklist Template` : 'Checklist Template';

    const headerData = {
        version: selectedItem?.checklistTemplate?.version || 1,
        checklistName: selectedItem?.checklistTemplate?.label || radioValue || 'Safety Inspection',
        category: selectedItem?.checklistTemplate?.category?.label || dropdownValue || 'Compliance',
        owner: selectedItem?.checklistTemplate?.type === 'workplace' || selectedItem?.checklistTemplate?.type === 'wokrplace' ? 'Workplace' : 
               selectedItem?.checklistTemplate?.type === 'organization' ? 'Organization' : 'Unknown',
        frequency: selectedItem?.checklistTemplate?.frequency?.label || 'Weekly',
        date: selectedItem?.completedAt ? new Date(selectedItem.completedAt).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }) : new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }),
    };

    return (
        <Paper elevation={2} sx={{ p: 0, overflow: 'hidden' }}>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Typography variant="body1"><strong>Version:</strong> {headerData.version}</Typography>
                    <Typography variant="body1"><strong>Checklist Name:</strong> {headerData.checklistName}</Typography>
                    <Typography variant="body1"><strong>Category:</strong> {headerData.category}</Typography>
                    <Typography variant="body1"><strong>Owner:</strong> {headerData.owner}</Typography>
                    <Typography variant="body1"><strong>Frequency:</strong> {headerData.frequency}</Typography>
                    <Typography variant="body1"><strong>Date:</strong> {headerData.date}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Checkbox
                            checked={selectedItem?.checklistTemplate?.signoffRequired || false}
                            disabled
                            color="primary"
                            size="small"
                        />
                        <Typography variant="body1"><strong>Sign Off</strong></Typography>
                    </Box>
                </Box>
            </Box>

            <TableContainer sx={{ maxHeight: 'calc(100vh - 250px)', overflowY: 'auto' }}>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            {columns.map((column) => (
                                <TableCell
                                    key={column.id}
                                    sx={{
                                        textAlign: column.id === 'section' ? 'left' : 'center',
                                        paddingLeft: column.id === 'section' ? '8px' : '1px',
                                    }}
                                >
                                    {column.header}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {items.map((item) => (
                            <React.Fragment key={item.id}>
                                <TableRow>
                                    {columns.map((column) => (
                                        <TableCell
                                            key={`${item.id}-${column.id}`}
                                            sx={{
                                                p: 1,
                                                textAlign: column.id === 'section' ? 'left' : 'center',
                                                paddingLeft: column.id === 'section' ? '8px' : '1px',
                                            }}
                                        >
                                            {column.id === 'correctAnswer' ? (
                                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                                    {(item.isCorrect !== undefined ? item.isCorrect : item.submittedAnswer === item.correctAnswer) ? (
                                                        <CheckCircleIcon color="success" fontSize="small" />
                                                    ) : (
                                                        <CancelIcon color="error" fontSize="small" />
                                                    )}
                                                </Box>
                                            ) : column.id === 'picLink' ? (
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    disabled={!item.picLink}
                                                    onClick={() => item.picLink && window.open(item.picLink, '_blank')}
                                                    sx={{ textTransform: 'none' }}
                                                >
                                                    View Picture
                                                </Button>
                                            ) : (
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        whiteSpace: 'pre-wrap',
                                                        wordBreak: 'break-word',
                                                    }}
                                                >
                                                    {item[column.id as keyof ChecklistItem] as string}
                                                </Typography>
                                            )}
                                        </TableCell>
                                    ))}
                                </TableRow>
                                <TableRow>
                                    <TableCell colSpan={columns.length} sx={{ p: 1, bgcolor: 'grey.50' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold', minWidth: '100px' }}>
                                                Comments:
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    whiteSpace: 'pre-wrap',
                                                    wordBreak: 'break-word',
                                                    flexGrow: 1,
                                                }}
                                            >
                                                {item.comment || 'No comments'}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            </React.Fragment>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {items.length === 0 && (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="body1" color="text.secondary">
                        No items in this checklist.
                    </Typography>
                </Box>
            )}

            <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    {/* <Button
                        style={{
                            backgroundColor: items.length ? 'rgb(68, 99, 136)' : 'gray',
                            color: 'white',
                            padding: '5px 15px',
                            fontSize: '12px',
                            display: 'flex',
                            alignItems: 'center',
                        }}
                        startIcon={<DownloadIcon />}
                        onClick={downloadJson}
                        disabled={items.length === 0}
                    >
                        Download JSON
                    </Button> */}
                </Box>
            </Box>
        </Paper>
    );
};

export default CompletedTemplatePage;